import { useEffect, useMemo, useState } from "react";
import type { Exercise } from "../../types";
import ClozeExercise from "./ClozeExercise";
import MultipleChoiceExercise from "./MultipleChoiceExercise";
import FixSentenceExercise from "./FixSentenceExercise";
import SpeakingExercise from "./SpeakingExercise";
import ReadingExercise from "./ReadingExercise";
import WritingExercise from "./WritingExercise";
import GlossaryText from "./GlossaryText";

interface Props {
  exercises: Exercise[];
  onItemDone?: () => void;     // fired after each attempt so the page can refresh counts
  onSetComplete?: () => void;  // fired once a set is fully cleared, to auto-advance category
}

const categoryDot: Record<string, string> = {
  grammar: "bg-warning",
  vocabulary: "bg-terracotta",
  syntax: "bg-success",
  pronunciation: "bg-error",
  reading: "bg-accent",
  writing: "bg-text-secondary",
};

const typeLabel: Record<string, string> = {
  cloze: "Fill the blank",
  multiple_choice: "Choose one",
  fix_sentence: "Correct it",
  repeat_aloud: "Speak",
  free_speak: "Speak",
  read_aloud: "Read aloud",
  read_comprehension: "Reading",
  write_prompt: "Write",
};

function ExerciseBody({ exercise, onDone, onSkip }: { exercise: Exercise; onDone: (p: boolean) => void; onSkip?: () => void }) {
  switch (exercise.exercise_type) {
    case "multiple_choice":
      return <MultipleChoiceExercise exercise={exercise} onDone={onDone} onSkip={onSkip} />;
    case "fix_sentence":
      return <FixSentenceExercise exercise={exercise} onDone={onDone} onSkip={onSkip} />;
    case "repeat_aloud":
    case "free_speak":
    case "read_aloud":
      return <SpeakingExercise exercise={exercise} onDone={onDone} onSkip={onSkip} />;
    case "read_comprehension":
      return <ReadingExercise exercise={exercise} onDone={onDone} onSkip={onSkip} />;
    case "write_prompt":
      return <WritingExercise exercise={exercise} onDone={onDone} onSkip={onSkip} />;
    case "cloze":
    default:
      return <ClozeExercise exercise={exercise} onDone={onDone} onSkip={onSkip} />;
  }
}

export default function ExerciseRunner({ exercises, onItemDone, onSetComplete }: Props) {
  const [queue, setQueue] = useState<Exercise[]>(exercises);
  const [index, setIndex] = useState(0);
  const [passedCount, setPassedCount] = useState(0);
  const [failed, setFailed] = useState<Exercise[]>([]);
  const [skippedIds, setSkippedIds] = useState<Set<number>>(new Set());

  // A set is "cleared" when every item was answered correctly, or when the only
  // unpassed items were skipped (not wrong answers). In both cases, advance.
  const allSkips = failed.length > 0 && failed.every((e) => skippedIds.has(e.id));
  const setCleared = index >= queue.length && queue.length > 0 && (failed.length === 0 || allSkips);
  useEffect(() => {
    if (!setCleared || !onSetComplete) return;
    const t = setTimeout(onSetComplete, 1400);
    return () => clearTimeout(t);
  }, [setCleared, onSetComplete]);

  // Reset when the incoming set changes (e.g. switching category tab).
  const signature = useMemo(() => exercises.map((e) => e.id).join(","), [exercises]);
  const [seenSignature, setSeenSignature] = useState(signature);
  if (signature !== seenSignature) {
    setSeenSignature(signature);
    setQueue(exercises);
    setIndex(0);
    setPassedCount(0);
    setFailed([]);
    setSkippedIds(new Set());
  }

  if (queue.length === 0) return null;

  const handleDone = (exercise: Exercise, passed: boolean) => {
    if (passed) setPassedCount((c) => c + 1);
    else setFailed((f) => [...f, exercise]);
    onItemDone?.();
    setIndex((i) => i + 1);
  };

  const skip = () => {
    const current = queue[index];
    setFailed((f) => [...f, current]);
    setSkippedIds((s) => new Set(s).add(current.id));
    onItemDone?.();
    setIndex((i) => i + 1);
  };

  // End-of-set summary
  if (index >= queue.length) {
    return (
      <div className="card px-6 py-8 text-center animate-scale-in">
        <div className="text-[2rem] font-display font-semibold text-ink mb-1">
          {passedCount} / {queue.length}
        </div>
        <p className="text-text-tertiary text-sm mb-5">
          {failed.length === 0 ? "Perfect set — bravissimo!" : `${failed.length} to review.`}
        </p>
        {failed.length > 0 && (
          <button
            className="btn-primary text-sm"
            onClick={() => {
              setQueue(failed);
              setIndex(0);
              setPassedCount(0);
              setFailed([]);
            }}
          >
            Practice {failed.length} again
          </button>
        )}
      </div>
    );
  }

  const current = queue[index];

  return (
    <div className="animate-fade-in">
      {/* Progress */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex-1 h-1.5 rounded-full bg-surface2 overflow-hidden">
          <div
            className="h-full bg-success transition-[width] duration-300"
            style={{ width: `${((index + 1) / queue.length) * 100}%` }}
          />
        </div>
        <span className="text-xs text-text-tertiary tabular-nums">{index + 1} / {queue.length}</span>
      </div>

      <div className="card px-6 py-6">
        <div className="flex items-center gap-2 mb-4">
          <span className={`w-2.5 h-2.5 rounded-full ${categoryDot[current.category] ?? "bg-accent"}`} />
          <span className="text-[0.625rem] uppercase tracking-[0.12em] font-semibold text-text-tertiary">
            {current.category} · {typeLabel[current.exercise_type] ?? current.exercise_type}
          </span>
        </div>
        {current.prompt && current.exercise_type !== "read_comprehension" && current.exercise_type !== "read_aloud" && (
          <p className="text-lg text-text-secondary mb-4 leading-relaxed">
            <GlossaryText text={current.prompt} />
          </p>
        )}
        <ExerciseBody key={current.id} exercise={current} onDone={(p) => handleDone(current, p)} onSkip={skip} />
      </div>
    </div>
  );
}
